import { useState, useEffect } from "react";
import {
  Link as LinkIcon, Plus, Trash2, RefreshCw, CheckCircle, XCircle,
  AlertCircle, Search, Edit2, Save, X, ExternalLink, Tag
} from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogLinksManager = ({ agent }) => {
  const [blogLinks, setBlogLinks] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, categories: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    url: "",
    description: "",
    keywords: "",
    category: "",
    priority: 5,
    is_active: true
  });

  useEffect(() => {
    fetchBlogLinks();
    fetchStats();
  }, []);

  const fetchBlogLinks = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/blog-links`, {
        params: { search: searchQuery || undefined, requester_id: agent?.id }
      });
      setBlogLinks(response.data.items || []);
    } catch (error) {
      logger.error("Error fetching blog links:", error);
      showMessage("error", "Failed to load blog links");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/blog-links/stats`, {
        params: { requester_id: agent?.id }
      });
      setStats(response.data);
    } catch (error) {
      logger.error("Error fetching stats:", error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.url.trim()) {
      showMessage("error", "Title and URL are required");
      return;
    }

    // Validate URL
    if (!formData.url.startsWith("http://") && !formData.url.startsWith("https://")) {
      showMessage("error", "URL must start with http:// or https://");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        keywords: formData.keywords.split(",").map(k => k.trim()).filter(Boolean),
        requester_id: agent?.id
      };

      if (editingId) {
        await axios.put(`${API}/blog-links/${editingId}`, payload);
        showMessage("success", "Blog link updated successfully");
      } else {
        await axios.post(`${API}/blog-links`, payload);
        showMessage("success", "Blog link added successfully");
      }

      resetForm();
      await fetchBlogLinks();
      await fetchStats();
    } catch (error) {
      logger.error("Error saving blog link:", error);
      showMessage("error", error.response?.data?.detail || "Failed to save blog link");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (blogLink) => {
    setFormData({
      title: blogLink.title,
      url: blogLink.url,
      description: blogLink.description || "",
      keywords: (blogLink.keywords || []).join(", "),
      category: blogLink.category || "",
      priority: blogLink.priority || 5,
      is_active: blogLink.is_active !== false
    });
    setEditingId(blogLink.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog link?")) return;

    setIsLoading(true);
    try {
      await axios.delete(`${API}/blog-links/${id}`, {
        params: { requester_id: agent?.id }
      });
      showMessage("success", "Blog link deleted successfully");
      await fetchBlogLinks();
      await fetchStats();
    } catch (error) {
      logger.error("Error deleting blog link:", error);
      showMessage("error", "Failed to delete blog link");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (blogLink) => {
    try {
      await axios.put(`${API}/blog-links/${blogLink.id}`, {
        is_active: !blogLink.is_active,
        requester_id: agent?.id
      });
      // Update local state
      setBlogLinks(links =>
        links.map(l =>
          l.id === blogLink.id ? { ...l, is_active: !l.is_active } : l
        )
      );
      await fetchStats();
    } catch (error) {
      logger.error("Error toggling blog link:", error);
      showMessage("error", "Failed to update blog link");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      url: "",
      description: "",
      keywords: "",
      category: "",
      priority: 5,
      is_active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBlogLinks();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-blue-600" />
            Blog Links
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage blog/resource links that are included in chat responses when relevant
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-[var(--g6-primary)] hover:bg-[#008A44]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Blog Link
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Links</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            <p className="text-sm text-gray-500">Active</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-gray-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
            <p className="text-sm text-gray-500">Inactive</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{stats.categories?.length || 0}</p>
            <p className="text-sm text-gray-500">Categories</p>
          </CardContent>
        </Card>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === "success"
            ? "bg-green-50 border border-green-200 text-green-800"
            : "bg-red-50 border border-red-200 text-red-800"
        }`}>
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">
              {editingId ? "Edit Blog Link" : "Add New Blog Link"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Salesforce Integration Guide"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    URL <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://www.gate6.com/blog/salesforce"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the blog content..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Keywords
                    <span className="text-gray-400 font-normal ml-1">(comma-separated)</span>
                  </label>
                  <Input
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="salesforce, crm, integration"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The bot will include this link when users ask about these keywords
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <Input
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., services, tutorials"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Priority
                    <span className="text-gray-400 font-normal ml-1">(1-10)</span>
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 5 })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <label className="text-sm">Active (included in responses)</label>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={isLoading} className="bg-[var(--g6-primary)]">
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingId ? "Update" : "Add"} Blog Link
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search blog links..."
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="outline">
          Search
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setSearchQuery("");
            fetchBlogLinks();
          }}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </form>

      {/* Blog Links List */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-gray-700">Blog</th>
                <th className="text-left p-4 text-sm font-medium text-gray-700">Keywords</th>
                <th className="text-center p-4 text-sm font-medium text-gray-700">Priority</th>
                <th className="text-center p-4 text-sm font-medium text-gray-700">Active</th>
                <th className="text-right p-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogLinks.map((link) => (
                <tr key={link.id} className="border-b hover:bg-gray-50">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-gray-900">{link.title}</p>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {link.url.length > 50 ? link.url.substring(0, 50) + "..." : link.url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {link.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{link.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {(link.keywords || []).slice(0, 3).map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                      {(link.keywords || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{link.keywords.length - 3} more
                        </Badge>
                      )}
                    </div>
                    {link.category && (
                      <Badge variant="outline" className="text-xs mt-1">
                        <Tag className="w-3 h-3 mr-1" />
                        {link.category}
                      </Badge>
                    )}
                  </td>
                  <td className="p-4 text-center">
                    <Badge
                      className={`${
                        link.priority >= 8
                          ? "bg-green-100 text-green-800"
                          : link.priority >= 5
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {link.priority}/10
                    </Badge>
                  </td>
                  <td className="p-4 text-center">
                    <Switch
                      checked={link.is_active !== false}
                      onCheckedChange={() => handleToggleActive(link)}
                    />
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(link)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(link.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {blogLinks.length === 0 && (
            <div className="text-center py-12">
              <LinkIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No blog links added yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Click "Add Blog Link" to add resources that will be included in chat responses
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Text */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            How Blog Links Work
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>
              When a user asks a question that matches any of the keywords, the bot will naturally include the blog link in its response.
            </li>
            <li>
              For example, if you add a blog about Salesforce with keywords "salesforce, crm, integration", when someone asks "Do you work with Salesforce?", the bot will mention and link to your blog.
            </li>
            <li>
              Higher priority links (8-10) are more likely to be included when multiple links match.
            </li>
            <li>
              Links are matched based on keywords in the title, description, and keyword list.
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default BlogLinksManager;
